import { useMemo } from "react";
import { Clock, GitMerge } from "lucide-react";
import FeedbackButtons from "@/components/FeedbackButtons";

function groupByTheme(questions) {
  const groups = new Map();
  for (const q of questions) {
    const key = q.theme_label || "Research";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(q);
  }
  return Array.from(groups.entries());
}

export default function QuestionsView({
  questions,
  unlocked,
  caseId,
  problemType,
}) {
  const grouped = useMemo(() => groupByTheme(questions), [questions]);
  const top5Ids = new Set(questions.slice(0, 5).map((q) => q.id));

  return (
    <div className="space-y-6" data-testid="questions-view">
      <div className="rounded-2xl border border-white/10 bg-gradient-to-b from-[#12181A] to-[#0F1416] p-6 sm:p-8">
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#35D07F]">
          Research plan
        </div>
        <h2 className="mt-3 text-3xl sm:text-4xl font-black tracking-tight text-white leading-[1.05]">
          This decision needs{" "}
          <span className="text-[#35D07F]">~{questions.length}</span>{" "}
          research questions.
        </h2>
        <p className="mt-3 text-[#A0AAB2] text-base sm:text-lg leading-relaxed max-w-2xl">
          Here are the sharpest {questions.length} — and the answers to the{" "}
          <span className="text-white font-medium">5 that matter most</span>.
        </p>
      </div>

      {/* Masonry-style layout so cards auto-height to content */}
      <div className="columns-1 lg:columns-2 gap-4 [column-fill:_balance]">
        {grouped.map(([theme, items], gi) => (
          <div
            key={theme}
            className="mb-4 break-inside-avoid rounded-2xl border border-white/10 bg-[#12181A] p-5"
            data-testid={`theme-group-${gi}`}
          >
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#35D07F]">
                {theme}
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">
                {items.length} {items.length === 1 ? "question" : "questions"}
              </div>
            </div>
            <ul className="mt-4 space-y-4">
              {items.map((q) => (
                <li
                  key={q.id}
                  data-testid={`question-${q.rank}`}
                  className="text-white/90 flex gap-3"
                >
                  <span className="text-[11px] font-mono text-white/30 pt-1 w-6 shrink-0">
                    {String(q.rank).padStart(2, "0")}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-3">
                      <span className="flex-1 text-[15px] leading-relaxed">
                        {q.text}
                      </span>
                      {caseId && (
                        <FeedbackButtons
                          caseId={caseId}
                          itemType="question"
                          itemId={q.id}
                          itemText={q.text}
                          themeLabel={q.theme_label}
                          problemType={problemType}
                        />
                      )}
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                      {top5Ids.has(q.id) && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-[#35D07F]/12 text-[#35D07F] text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5">
                          Top 5 · answered
                        </span>
                      )}
                      {q.time_sensitive && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-white/5 text-white/70 text-[10px] uppercase tracking-wider px-2 py-0.5">
                          <Clock className="h-2.5 w-2.5" strokeWidth={2.5} /> time-sensitive
                        </span>
                      )}
                      {q.is_intersection && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-white/5 text-white/70 text-[10px] uppercase tracking-wider px-2 py-0.5">
                          <GitMerge className="h-2.5 w-2.5" strokeWidth={2.5} /> intersection
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
